<template>
  <v-container fluid justify-start :class="showGoBack ? '' : 'py-1'">
    <v-card class="elevation-1" 
      color="transparent" 
      :class="showGoBack ? 'mb-2' : 'mt-2'"> 

      <v-toolbar dark dense flat :color="appColor_Resident">

        <app-back-button v-if="showGoBack" v-bind:color="appColor_ResidentDark" ref="backbutton" />

        <v-toolbar-title>
          <span :class="toolbarTitleClass" v-html="getExpression('Heading', 'ManageResidentExperience')"></span>
        </v-toolbar-title>

        <v-spacer />

        <template v-if="!isSmallDevice">
          
          <!-- Toolbar Buttons -->
          <v-toolbar-items>

            <!-- Add User -->
            <v-btn
              v-if="isGenie || (isAbby && !isResidentManager)"
              :color="appColor_ResidentDark"
              @click="addUser()"
              :width="buttonWidthToolbar"
              class="elevation-0"
              >
              <span class="font-weight-medium">{{ addUserButtonText }}</span>
            </v-btn>

            <!-- Edit Resident -->
            <v-btn 
              dark 
              :color="appColor_ResidentDark" 
              @click="edit()"
              :width="buttonWidthToolbar"
              class="ml-3 elevation-0 px-1">
              <span class="font-weight-medium">{{ getExpression('Button', 'EditResident') }}</span>
            </v-btn>
            <input v-show="false" type="file" accept='image/*' ref="picture" @change="uploadPicture()">
          </v-toolbar-items>
        </template>

        <!-- Ellipsis Menu -->
        <template v-else>
          <v-menu bottom left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                small
                :color="appColor_ResidentDark"
                v-bind="attrs"
                v-on="on"
                class="elevation-0"
                >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="addUser()">
                <v-list-item-title>
                  <span class="font-weight-medium">{{ addUserButtonText }}</span>
                </v-list-item-title>
              </v-list-item>

              <v-list-item @click="edit()">
                <v-list-item-title>
                  <span class="font-weight-medium">{{ getExpression('Button', 'EditResident') }}</span> 
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-toolbar>

      <!-- Main Content -->
      <v-container fluid class="mt-0 pa-0">
        <v-card class="px-1 pb-2">

          <v-row>
            <v-col cols="12" sm="4">
              <!-- General Info -->
              <general-info class="mb-1" />
            </v-col>

            <!-- Missing Picture Alert -->
            <template v-if="isMissingPicture && isGenie">
              <v-col cols="12" sm="8">
                <v-alert
                  border="top"
                  colored-border
                  type="info"
                  elevation="2"
                  class="mx-2 mt-2 mb-0"
                  >
                  <v-row align="center">
                    <v-col class="grow pr-1">
                      <span class="body-2 font-weight-normal">To add a <b>picture</b> for this resident, click <b>EDIT RESIDENT</b>, then <b>ADD PICTURE</b>.</span>
                    </v-col>
                    <v-col class="shrink pl-0 py-0">
                      <v-icon color="#333" x-large class="mr-3">arrow_upward</v-icon>
                    </v-col>
                  </v-row>

                </v-alert>
              </v-col>
            </template>
          </v-row>
          
          <!-- GENIE operations -->
          <template v-if="isGenie">
            <v-row no-gutters>
              <v-btn :width="buttonWidth" large @click="gotoRoute('MessageManager')" dark :color="appColor_ResidentDark" :class="buttonClass">
                {{ getExpression('Button', 'Messages') }}
              </v-btn>
              <template v-if="!isAdmin">
                <v-btn :width="buttonWidth" large @click="composeMessage()" dark :color="appColor_ResidentDark" :class="buttonClass" >
                  {{ getExpression('Button', 'SendMessage') }}
                </v-btn>
              </template>
              <v-btn :width="buttonWidth" large @click="gotoRoute('GenieResidentContacts')" dark :color="appColor_ResidentDark" :class="buttonClass">
                {{ getExpression('Button', 'Contacts') }}
              </v-btn>
              <v-btn :width="buttonWidth" large @click="gotoRoute('ResidentPlaylistManager')" dark :color="appColor_ResidentDark" :class="buttonClass">
                {{ getExpression('Button', 'MemoryAlbums') }}
              </v-btn>
              <v-btn :width="buttonWidth" large @click="gotoRoute('ResidentMediaManager')" dark :color="appColor_ResidentDark" :class="buttonClass">
                {{ getExpression('Button', 'Library') }}
              </v-btn>
              <v-btn :width="buttonWidth" large @click="gotoRoute('ResidentMetrics')" dark :color="appColor_Resident" :class="buttonClass">
                {{ getExpression('Button', 'Metrics') }}
              </v-btn>
            </v-row>
          </template>

          <!-- ABBY operations -->
          <template v-if="isAbby">
            <v-btn :width="buttonWidth" large @click="gotoRoute('AbbyResidentProfileManager')" dark :color="appColor_ResidentDark" :class="buttonClass">
              {{ getExpression('Button', 'ResidentPlaylists') }}
            </v-btn>
            <v-btn v-if="!isResidentManager" :width="buttonWidth" large @click="gotoRoute('AbbyResidentFacilitators')" dark :color="appColor_ResidentDark" :class="buttonClass">
              {{ getExpression('Button', 'Facilitators') }}
            </v-btn>
          </template>

        </v-card>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { AppColor, KioskTypeId, DashboardContext, LanguageId } from '@/config.js'
import AppBackButton from '@/components/App/AppBackButton'
import GeneralInfo from '@/components/ResidentDashboard/GeneralInfo.vue'

export default {
  name: 'ResidentDashboard',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    GeneralInfo,
    AppBackButton
  },
  data() {
    return {

      // colors
      appColor_Light: AppColor.Light,
      appColor_Progress: AppColor.Progress,
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      // dashboard contexts
      context_ResidentDashboard: DashboardContext.ResidentDashboard
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'languages',
      'activeResident',
      'isAdmin',
      'isKioskManager',
      'isResidentManager',
      'locale',
      'previousRoute']),
    kiosk() {
      const unknownKiosk = {
        id: 0,
        name: 'Unknown Kiosk',
        codeName: 'UNKNOWN',
        typeId: 0
      }
      if (this.isNullOrUndefined(this.activeResident)) return unknownKiosk
      return this.activeResident.kiosk
    },
    isFromKioskDashboard () {
      return this.previousRoute.indexOf('KioskDashboard') >= 0
        || (this.previousRoute.indexOf('Home') >= 0 && this.isKioskManager)
    },
    languageId () {
      return this.profile.languageId
    },
    hasMultipleResidents() {
      if (this.isNullOrUndefined(this.profile.residentIds)) return false
      return this.profile.residentIds.length > 1
    },
    showGoBack () {
      return this.hasMultipleResidents || !this.isResidentManager
    },
    isGenie() {
      return this.kiosk.typeId === this.kioskTypeId_Genie
    },
    isAbby() {
      return this.kiosk.typeId === this.kioskTypeId_Abby
    },
    toolbarTitleClass () {
      return this.isSmallDevice
        ? 'font-weight-light body-1 black--text'
        : 'font-weight-light black--text'
    },
    addUserButtonText () {
      return this.isGenie
        ? this.getExpression('Button', 'AddContact')
        : this.getExpression('Button', 'AddFamilyFacilitator')
    },
    buttonClass () {
      return 'ml-2 mb-2 rounded-0'
    },
    buttonWidth () {
      return this.languageId === LanguageId.English
        ? 190
        : 220
    },
    buttonWidthToolbar () {
      return this.isGenie
      ? this.languageId === LanguageId.English
        ? 150
        : 230
      : this.languageId === LanguageId.English
        ? 170
        : 230
    },
    residentPictureUrl () {
      if (this.activeResident === null) return null
      if (this.isNullOrUndefined(this.activeResident.pictureBase64)) return null
      return this.activeResident.pictureBase64
    },
    isMissingPicture () {
      return this.residentPictureUrl === null
    }
  },
  methods: {
    edit () {
      const self = this
      self.$resident.edit({
        kioskTypeId: self.kiosk.typeId,
        showAddUser: false,
        onUpdate: (resident) => {
          self.$store.dispatch('updateResident', { resident: resident })
          if (self.isFromKioskDashboard) {
            self.$store.dispatch('updateKioskResident', { resident: resident })
            const kioskKeyNum = { id: resident.kioskKeyTagId, residentId: resident.id }
            self.$store.dispatch('updateKioskKeyNum', { kioskKeyNum })
          }
        },
        onDelete: (resident) => {
          const id = resident.id

          // remove the resident from vues store
          self.$store.dispatch('removeKioskResident', { id })

         // update kioskKeyNums in vuex store to make the deleted resident's key # available
          if (resident.kioskKeyTagId > 0) {
            const kioskKeyNum = {
              id: resident.kioskKeyTagId,
              residentId: null
            }
            self.$store.dispatch('updateKioskKeyNum', { kioskKeyNum })
          }
          
          self.$refs.backbutton.$el.click()
        },
        onChangePicture: (resident) => {
          self.$store.dispatch('updateResident', { resident: resident })
          if (!self.isResidentManager) {
            resident.picture = resident.pictureBase64
            self.$store.dispatch('updateKioskResident', { resident: resident })
          }
        }
      })
    },

    addUser () {
      const self = this
      self.$user.add({
        context: self.context_ResidentDashboard
      })
    },

    composeMessage () {
      const self = this
      const messageResidents = [{
        id: self.activeResident.id,
        name: self.activeResident.name,
        kioskTypeId: self.activeResident.kioskTypeId,
        picture: null
      }]

      self.$message.compose({
        residents: messageResidents,
        isMultipleResidents: false
      })
    },

    gotoRoute (name) {
      this.$store.dispatch('pushRoute', { name: name })
      this.$router.push({ name: name})
    }
  }
}
</script>
